import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import type { MonthlyTrendData } from '../../types';

interface TrendLineChartProps {
  data: MonthlyTrendData[];
  height?: number;
  showLegend?: boolean;
  showArea?: boolean;
  dataKeys?: ('sanctioned' | 'received' | 'spent' | 'utilization' | 'recurringSpent' | 'nonRecurringSpent')[];
  colors?: Record<string, string>;
}

const TrendLineChart: React.FC<TrendLineChartProps> = ({
  data,
  height = 350,
  showLegend = true,
  showArea = false,
  dataKeys = ['sanctioned', 'spent'],
  colors = {
    sanctioned: '#6B1786',
    received: '#059669',
    spent: '#10b981',
    utilization: '#f59e0b',
    recurringSpent: '#3b82f6',
    nonRecurringSpent: '#ec4899',
  },
}) => {
  const formatCurrency = (value: number) => {
    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(1)}Cr`;
    } else if (value >= 100000) {
      return `₹${(value / 100000).toFixed(1)}L`;
    }
    return `₹${value.toLocaleString()}`;
  };

  const formatLabel = (key: string) => {
    const labels: Record<string, string> = {
      sanctioned: 'Sanctioned',
      received: 'Received',
      spent: 'Spent',
      utilization: 'Utilization %',
      recurringSpent: 'Recurring',
      nonRecurringSpent: 'Non-Recurring',
    };
    return labels[key] || key;
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white shadow-lg rounded-lg p-3 border border-gray-200">
          <p className="font-semibold text-gray-900 mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.stroke }}
              />
              <span className="text-gray-600">{entry.name}:</span>
              <span className="font-medium text-gray-900">
                {entry.dataKey === 'utilization'
                  ? `${entry.value.toFixed(1)}%`
                  : formatCurrency(entry.value)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const chartData = data.map((item) => ({
    ...item,
    name: item.month.includes('-') && item.month.length > 3 ? item.month : `${item.month} ${item.year}`,
  }));

  const ChartComponent = showArea ? AreaChart : LineChart;

  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ChartComponent
          data={chartData}
          margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
          />
          <YAxis
            yAxisId="left"
            tickFormatter={formatCurrency}
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
          />
          {dataKeys.includes('utilization') && (
            <YAxis
              yAxisId="right"
              orientation="right"
              tickFormatter={(v) => `${v}%`}
              domain={[0, 100]}
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
            />
          )}
          <Tooltip content={<CustomTooltip />} />
          {showLegend && <Legend />}
          
          {dataKeys.map((key) =>
            showArea ? (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                name={formatLabel(key)}
                stroke={colors[key]}
                fill={colors[key]}
                fillOpacity={0.1}
                strokeWidth={2}
                yAxisId={key === 'utilization' ? 'right' : 'left'}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
            ) : (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                name={formatLabel(key)}
                stroke={colors[key]}
                strokeWidth={2}
                yAxisId={key === 'utilization' ? 'right' : 'left'}
                dot={{ r: 3, fill: colors[key] }}
                activeDot={{ r: 5 }}
              />
            )
          )}
        </ChartComponent>
      </ResponsiveContainer>
    </div>
  );
};

export default TrendLineChart;
